export interface FieldBox {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  unit?: string;
}

export interface OverlayRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function boxToOverlay(box: FieldBox, pageWidth: number, pageHeight: number): OverlayRect | null {
  if (
    pageWidth <= 0 ||
    pageHeight <= 0 ||
    box.x == null ||
    box.y == null ||
    box.width == null ||
    box.height == null ||
    box.width <= 0 ||
    box.height <= 0
  ) {
    return null;
  }

  const unit = (box.unit ?? inferUnit(box.x, box.y, box.width, box.height)).toLowerCase();
  let x = box.x;
  let y = box.y;
  let width = box.width;
  let height = box.height;

  if (unit === 'normalized' || unit === 'norm' || unit === 'relative' || unit === 'ratio') {
    // already 0–1
  } else if (unit === 'inch' || unit === 'inches' || unit === 'in') {
    const pageWidthInches = pageWidth / 72;
    const pageHeightInches = pageHeight / 72;
    x /= pageWidthInches;
    y /= pageHeightInches;
    width /= pageWidthInches;
    height /= pageHeightInches;
  } else {
    x /= pageWidth;
    y /= pageHeight;
    width /= pageWidth;
    height /= pageHeight;
  }

  const left = clamp01(x) * 100;
  const top = clamp01(y) * 100;
  const overlayWidth = Math.min(100 - left, Math.max(0.15, width * 100));
  const overlayHeight = Math.min(100 - top, Math.max(0.15, height * 100));

  if (overlayWidth <= 0 || overlayHeight <= 0) {
    return null;
  }

  return { left, top, width: overlayWidth, height: overlayHeight };
}

/** True when the box came from Document Intelligence word polygons, not an LLM guess. */
export function isOcrFieldBox(box: FieldBox | null | undefined): box is FieldBox {
  if (
    box == null
    || box.x == null
    || box.y == null
    || box.width == null
    || box.height == null
    || box.width <= 0
    || box.height <= 0
  ) {
    return false;
  }

  const unit = (box.unit ?? '').toLowerCase();
  if (unit === 'inch' || unit === 'inches' || unit === 'in') {
    return true;
  }

  return [box.x, box.y, box.width, box.height].some((value) => decimalPlaces(value) > 2);
}

function decimalPlaces(value: number): number {
  const text = String(value);
  const index = text.indexOf('.');
  return index < 0 ? 0 : text.length - index - 1;
}

function inferUnit(x: number, y: number, width: number, height: number): string {
  const max = Math.max(x + width, y + height, width, height);
  if (max <= 1.5) {
    return 'normalized';
  }
  if (max <= 22) {
    return 'inch';
  }
  return 'pt';
}

function clamp01(value: number): number {
  if (Number.isNaN(value)) {
    return 0;
  }
  return Math.min(1, Math.max(0, value));
}
